import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../../components/ui/dialog';
import { Plus, Loader2, Users, BookOpen, Trash2, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';

const ClassesPage = () => {
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningClass, setAssigningClass] = useState(null);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  const [assigningLoading, setAssigningLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classesRes, teachersRes] = await Promise.all([
        api.get('/classes'),
        api.get('/users?role=teacher')
      ]);
      setClasses(classesRes.data);
      setTeachers(teachersRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter class name');
      return;
    }

    setSaving(true);
    try {
      await api.post('/classes', formData);
      toast.success('Class created successfully');
      setDialogOpen(false);
      setFormData({ name: '', description: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create class');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAssignDialog = (cls) => {
    setAssigningClass(cls);
    // Pre-select currently assigned teachers
    const assignedTeacherIds = (cls.assigned_teachers || []).map(t => t.id);
    setSelectedTeachers(assignedTeacherIds);
    setAssignDialogOpen(true);
  };

  const handleSaveTeacherAssignments = async () => {
    if (!assigningClass) return;
    
    setAssigningLoading(true);
    try {
      // For each selected teacher, ensure they have this class assigned
      // We need to update each teacher's class assignments
      for (const teacherId of selectedTeachers) {
        // Get teacher's current classes
        const res = await api.get(`/teachers/${teacherId}/classes`);
        const currentClassIds = res.data.map(c => c.id);
        
        // If this class is not already assigned, add it
        if (!currentClassIds.includes(assigningClass.id)) {
          await api.post(`/teachers/${teacherId}/assign-classes`, {
            section_ids: [...currentClassIds, assigningClass.id]
          });
        }
      }
      
      // For teachers that were unselected, remove this class from their assignments
      const previousTeacherIds = (assigningClass.assigned_teachers || []).map(t => t.id);
      for (const teacherId of previousTeacherIds) {
        if (!selectedTeachers.includes(teacherId)) {
          // Get teacher's current classes and remove this one
          const res = await api.get(`/teachers/${teacherId}/classes`);
          const currentClassIds = res.data.map(c => c.id).filter(id => id !== assigningClass.id);
          await api.post(`/teachers/${teacherId}/assign-classes`, {
            section_ids: currentClassIds
          });
        }
      }
      
      toast.success('Teacher assignments updated successfully');
      setAssignDialogOpen(false);
      setAssigningClass(null);
      setSelectedTeachers([]);
      fetchData(); // Refresh data
    } catch (err) {
      let errorMessage = 'Failed to update assignments';
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        }
      }
      toast.error(errorMessage);
    } finally {
      setAssigningLoading(false);
    }
  };

  const toggleTeacher = (teacherId) => {
    setSelectedTeachers(prev => 
      prev.includes(teacherId) 
        ? prev.filter(id => id !== teacherId)
        : [...prev, teacherId]
    );
  };

  const handleDelete = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    
    try {
      await api.delete(`/classes/${classId}`);
      toast.success('Class deleted successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete class');
    }
  };

  const getAssignedTeachersDisplay = (cls) => {
    const assigned = cls.assigned_teachers || [];
    if (assigned.length === 0) return 'No teachers assigned';
    if (assigned.length === 1) return assigned[0].name;
    return `${assigned.length} teachers assigned`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Classes</h1>
            <p className="text-slate-500">Manage classes and assign teachers</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-class-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Class
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Class</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Class Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., 1A, 2B, 10C"
                    data-testid="class-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., English Literature"
                    data-testid="class-description-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="save-class-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Create Class
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </div>
        ) : classes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <BookOpen className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No classes created yet</p>
              <p className="text-sm text-slate-400">Click "Add Class" to create your first class</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {classes.map((cls) => (
              <Card key={cls.id} className="card-hover">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900">
                        <BookOpen className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{cls.name}</CardTitle>
                        <p className="text-sm text-slate-500">{cls.description || 'No description'}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(cls.id)}
                      className="text-rose-600 hover:text-rose-700"
                      data-testid={`delete-class-${cls.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Assigned Teachers Display */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Users className="h-4 w-4" />
                        <span>{getAssignedTeachersDisplay(cls)}</span>
                      </div>
                      
                      {/* Show teacher badges if multiple assigned */}
                      {(cls.assigned_teachers || []).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {(cls.assigned_teachers || []).map(teacher => (
                            <Badge 
                              key={teacher.id} 
                              variant="secondary"
                              className="text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300"
                            >
                              {teacher.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Assign Teachers Button */}
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => handleOpenAssignDialog(cls)}
                      data-testid={`assign-teachers-${cls.id}`}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      {(cls.assigned_teachers || []).length > 0 ? 'Manage Teachers' : 'Assign Teachers'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Assign Teachers Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={(open) => {
          setAssignDialogOpen(open);
          if (!open) {
            setAssigningClass(null);
            setSelectedTeachers([]);
          }
        }}>
          <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Assign Teachers to {assigningClass?.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Select teachers to assign to this class. Teachers will be able to manage students and content for assigned classes.
              </p>
              
              {teachers.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No teachers available. Please create teachers first.</p>
              ) : (
                <div className="border rounded-lg p-4 max-h-[300px] overflow-y-auto space-y-2">
                  {teachers.map(teacher => (
                    <div 
                      key={teacher.id} 
                      className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                      onClick={() => toggleTeacher(teacher.id)}
                    >
                      <Checkbox
                        id={`teacher-${teacher.id}`}
                        checked={selectedTeachers.includes(teacher.id)}
                        onCheckedChange={() => toggleTeacher(teacher.id)}
                      />
                      <div className="flex-1">
                        <label
                          htmlFor={`teacher-${teacher.id}`}
                          className="text-sm font-medium cursor-pointer"
                        >
                          {teacher.name}
                        </label>
                        <p className="text-xs text-slate-500">{teacher.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="pt-2 border-t">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Selected:</strong> {selectedTeachers.length} teacher(s)
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setAssignDialogOpen(false)}
                disabled={assigningLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveTeacherAssignments}
                disabled={assigningLoading}
                data-testid="save-teacher-assignments-btn"
              >
                {assigningLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Assignments
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ClassesPage;
